/**
 * Kill switches for field deployment - URL params and remote toggles
 */

export interface KillSwitches {
  disableServiceWorker: boolean;
  forceLiteMode: boolean;
  forceFullMode: boolean;
  disableVideo: boolean;
  disableMaps: boolean;
  disableAnimations: boolean;
}

// Parse URL parameters for kill switches
function parseUrlKillSwitches(): Partial<KillSwitches> {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  
  return {
    disableServiceWorker: params.has('noSW') || params.get('sw') === '0',
    forceLiteMode: params.has('lite') || params.get('lite') === '1',
    forceFullMode: params.has('full') || params.get('full') === '1',
    disableVideo: params.has('novideo') || params.get('video') === '0',
    disableMaps: params.has('nomaps') || params.get('maps') === '0',
    disableAnimations: params.has('noanim') || params.get('anim') === '0'
  };
}

// Remote kill switches (for emergency field deployment controls)
let remoteSwitches: Partial<KillSwitches> = {};

async function fetchRemoteKillSwitches(): Promise<void> {
  try {
    // Only attempt in production and with a timeout
    if (import.meta.env.DEV) return;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    const response = await fetch('/api/killswitches', {
      signal: controller.signal,
      cache: 'no-cache'
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      remoteSwitches = await response.json();
      console.debug('[KillSwitches] Remote switches loaded:', remoteSwitches);
    }
  } catch (error) {
    // Silently fail - kill switches are emergency only
    console.debug('[KillSwitches] Remote fetch failed (expected):', error);
  }
}

// Get combined kill switches (URL overrides remote)
export function getKillSwitches(): KillSwitches {
  const urlSwitches = parseUrlKillSwitches();
  
  return {
    disableServiceWorker: false,
    forceLiteMode: false,
    forceFullMode: false,
    disableVideo: false,
    disableMaps: false,
    disableAnimations: false,
    // Remote switches as defaults
    ...remoteSwitches,
    // URL switches override everything
    ...urlSwitches
  };
}

// Initialize remote switches fetch (non-blocking)
export function initKillSwitches(): void {
  if (typeof window !== 'undefined') {
    // Fetch remote switches in background
    fetchRemoteKillSwitches().catch(() => {
      // Ignore errors - this is purely optional
    });
  }
}

// Utility to check if a feature is disabled
export function isFeatureDisabled(feature: keyof KillSwitches): boolean {
  return getKillSwitches()[feature] === true;
}